import React, { useState } from "react";
import {
  HelpCircle,
  Mail,
  Send,
  ChevronDown,
  MessageSquare,
  CheckCircle,
} from "lucide-react";
import useContact from "../../hooks/useContact";
import "../../assets/css/support/support.css";

const SupportPage = () => {
  const user = JSON.parse(localStorage.getItem("user"));
  const [openIndex, setOpenIndex] = useState(null);
  const [form, setForm] = useState({
    name: user ? `${user.nom} ${user.prenom}` : "",
    email: user?.email || "",
    subject: "",
    message: "",
  });
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const { contactAdmin } = useContact();

  const faqList = [
    {
      question: "Comment publier une annonce ?",
      answer:
        "Cliquez sur « Commencer à vendre », choisissez une catégorie, ajoutez un titre, une description et des photos. Le prix de publication est calculé automatiquement selon le nombre de photos et le type de catégorie. Une fois validée, votre annonce est publiée immédiatement.",
    },
    {
      question: "Comment est calculé le prix de publication ?",
      answer:
        "Le prix dépend du nombre de photos ajoutées et du type de catégorie. Une annonce Standard accepte jusqu’à 2 photos, tandis qu’une annonce Premium peut aller jusqu’à 10 photos. Les catégories Premium ont un tarif plus élevé.",
    },
    {
      question:
        "Quelle est la différence entre une annonce Standard et Premium ?",
      answer:
        "Une annonce Standard permet jusqu’à 2 photos avec une visibilité normale. Une annonce Premium permet jusqu’à 10 photos et bénéficie d’une meilleure visibilité sur la plateforme.",
    },
    {
      question: "Comment contacter un vendeur ?",
      answer:
        "Depuis la fiche de l’annonce, cliquez sur l'icon « Message ». Vous pouvez envoyer un message ou appeler le vendeur directement selon les options disponibles.",
    },
    {
      question: "Quelles sont les méthodes de paiement acceptées ?",
      answer:
        "Les paiements peuvent être effectués par carte bancaire ou via les services Mobile Money (MVola, Orange Money, Airtel Money). Les informations demandées varient selon la méthode choisie.",
    },
    {
      question: "Qu’est-ce que le boost d’annonce ?",
      answer:
        "Le boost permet d’augmenter la visibilité de votre annonce pendant 1 jour. Une annonce boostée apparaît en priorité dans les résultats de recherche.",
    },
    {
      question: "Puis-je modifier une annonce après publication ?",
      answer:
        "Oui. Vous pouvez modifier le titre, la description, le prix et les photos de votre annonce depuis votre espace personnel.",
    },
    {
      question: "Comment gérer mes annonces ?",
      answer:
        "Dans la section « Mes annonces », vous pouvez consulter, modifier, archiver ou supprimer vos annonces à tout moment.",
    },
    {
      question: "Comment recevoir les notifications ?",
      answer:
        "Vous recevez des notifications en temps réel pour les messages ou interactions avec vos annonces. Les notifications non lues sont visibles dans votre menu.",
    },
    {
      question: "Comment contacter le support ?",
      answer:
        "Rendez-vous dans la section « Contact » depuis votre menu utilisateur pour nous envoyer un message. Notre équipe vous répondra rapidement.",
    },
  ];

  const toggleFAQ = (index) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  const handleFormChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleFormSubmit = (e) => {
    e.preventDefault();
    const response = contactAdmin(form);
    if (response) {
      setShowSuccessMessage(true);
      setForm({ subject: "", message: "" });
      setTimeout(() => {
        setShowSuccessMessage(false);
      }, 7000);
    }
  };

  return (
    <div className="support_page_content container">
      {showSuccessMessage && (
        <div className="support_page_toast">
          <CheckCircle size={24} className="support_toast_icon" />
          <div className="support_toast_content">
            <p className="support_toast_title">Message Envoyé !</p>
            <p className="support_toast_text">
              Nous avons bien reçu votre demande et vous répondrons dans les 24
              heures.
            </p>
          </div>
        </div>
      )}

      <div className="support_header">
        <HelpCircle size={48} className="support_header_icon" />
        <h1>Centre d'Aide & Support</h1>
        <p className="support_header_subtitle">
          Trouvez des réponses rapides dans notre FAQ ou envoyez-nous un message
          directement.
        </p>
      </div>

      <div className="support_content">
        <div className="support_faq_section">
          <h2 className="support_faq_title">
            <MessageSquare size={20} className="support_faq_title_icon" />{" "}
            Questions Fréquentes (FAQ)
          </h2>

          {faqList.map((item, index) => (
            <div
              key={index}
              className={`support_faq_item ${openIndex === index ? "open" : ""}`}
            >
              <div
                className="support_faq_question"
                onClick={() => toggleFAQ(index)}
              >
                <span>{item.question}</span>
                <ChevronDown
                  className={`support_faq_arrow ${
                    openIndex === index ? "support_faq_rotate_180" : ""
                  }`}
                  size={20}
                />
              </div>

              <div
                className={`support_faq_answer_container${
                  openIndex === index ? "active" : ""
                }`}
              >
                <div className="support_faq_answer">{item.answer}</div>
              </div>
            </div>
          ))}
        </div>

        <div className="support_contact_section">
          <h2 className="support_faq_title">
            <Mail size={20} className="support_faq_title_icon" /> Nous Contacter
          </h2>

          <p className="support_contact_intro">
            Vous n'avez pas trouvé votre réponse ? Envoyez-nous un message !
          </p>

          <form className="support_contact_form" onSubmit={handleFormSubmit}>
            <div className="support_contact_form_group">
              <label htmlFor="name">Votre nom</label>
              <input
                type="text"
                id="name"
                name="name"
                placeholder="Nom complet"
                value={form.name}
                onChange={handleFormChange}
                required
                className="input-field-v8"
              />
            </div>

            <div className="support_contact_form_group">
              <label htmlFor="email">Adresse email</label>
              <input
                type="email"
                id="email"
                name="email"
                placeholder="Adresse email"
                value={form.email}
                onChange={handleFormChange}
                required
                className="input-field-v8"
              />
            </div>

            <div className="support_contact_form_group">
              <label htmlFor="subject">Sujet</label>
              <input
                type="text"
                id="subject"
                name="subject"
                placeholder="Sujet de votre message"
                value={form.subject}
                onChange={handleFormChange}
                required
                className="input-field-v8"
              />
            </div>

            <div className="support_contact_form_group">
              <label htmlFor="message">Message</label>
              <textarea
                id="message"
                name="message"
                rows={5}
                placeholder="Écrivez votre message détaillé..."
                value={form.message}
                onChange={handleFormChange}
                required
                className="input-field-v8 textarea-v8"
              />
            </div>

            <button type="submit" className="support_btn_send_message">
              <Send size={20} /> Envoyer le message
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default SupportPage;
